import { createContext, useContext, type ReactNode } from "react";
import { DecisionEngine, Channel, Message } from ".";
import { TagsSchema } from "@hightouch/lib/customer-data/decision-engine/types";

export type DecisionEngineContext = {
  engine: DecisionEngine;
  channels: Array<Channel>;
  messages: Array<Message>;
  activeMessages: Array<Message>;
  messageTagsSchema: TagsSchema;
  variantTagsSchema: TagsSchema;
  channelTagsSchema: TagsSchema;
};
const DecisionEngineCtx = createContext<DecisionEngineContext | null>(null);

export function DecisionEngineProvider({
  value,
  children,
}: {
  value: DecisionEngineContext;
  children: ReactNode;
}) {
  return (
    <DecisionEngineCtx.Provider value={value}>
      {children}
    </DecisionEngineCtx.Provider>
  );
}

export function useDecisionEngineContext(): DecisionEngineContext {
  const ctx = useContext(DecisionEngineCtx);
  if (!ctx) {
    throw new Error(
      "useDecisionEngineContext must be used within a DecisionEngineProvider",
    );
  }
  return ctx;
}

/**
 * Returns a representative parent model for the decision engine, derived from
 * its flows.
 *
 * Transitional shim: some DE-shared resources (channel send events,
 * collections) still operate on a single parent model rather than per-flow.
 * Under the same-source constraint a DE's flows share one warehouse
 * connection; today they also share a single parent model, so the first
 * flow's parent model represents the DE. It can be removed once those surfaces
 * are scoped per flow.
 */
export function useDecisionEngineParentModel(): DecisionEngine["flows"][number]["parent_model"] {
  const { engine } = useDecisionEngineContext();
  return engine.flows.find((flow) => flow.parent_model)?.parent_model ?? null;
}
